import './trigger.scss';
import React, { useEffect, useState } from 'react';
import { Col, Form, InputGroup, Row } from 'react-bootstrap';
import { AbilityTriggerTypes } from '../../../enums/AbilityTriggerTypes';
import { Abilities } from '../../../models/Ability';
import { selectBaseStyles, selectRightInGroupStyles } from '@components';
import { useFormValidation } from './trigger.hooks';
import { TriggerWorkflowStepProps } from './TriggerWorkflowStepsProps';
import Select2 from 'react-select';

export function AbilityForm({
  isSubmitted,
  trigger,
  onChange,
  disabled,
}: TriggerWorkflowStepProps) {
  const [fallsBelow, setFallsBelow] = useState<any>(trigger.FallsBelow);
  const [risesAbove, setRisesAbove] = useState<any>(trigger.RisesAbove);
  const [
    conditionError,
    hasName,
    hasType,
    hasCondition,
    hasRisesAbove,
    hasFallsBelow,
    hasEntryExit,
    hasMap,
    hasPlace,
    hasFilter,
    isValid,
  ] = useFormValidation({ trigger });

  const unitOptions = GetUnits().map((u) => ({
    value: u.key,
    label: u.text,
  }));

  const selectedUnitOption =
    unitOptions.find((o) => String(o.value) === String(trigger.Units || GetUnits()[0]?.key)) ??
    null;

  const abilityOptions = Abilities.filter((a) => a.canTrigger === true).map((a) => ({
    value: a.id,
    label: a.name,
  }));

  const selectedAbilityOption =
    trigger.AbilityId && trigger.AbilityId > 0
      ? abilityOptions.find((o) => Number(o.value) === Number(trigger.AbilityId)) ?? null
      : null;

  const showConditionInvalid = !hasCondition && isSubmitted;

  // Single Validation Check
  const checkRangeInvalidity = (low: any, high: any) => {
    const l = parseFloat(low);
    const h = parseFloat(high);
    return !isNaN(l) && !isNaN(h) && l >= h;
  };

  // 2. UI Error State
  const isRangeInvalid = checkRangeInvalidity(fallsBelow, risesAbove);

  const displayError = isRangeInvalid
    ? 'Falls Below must be lower than Rises Above'
    : conditionError;

  useEffect(() => {
    setFallsBelow(trigger.FallsBelow);
    setRisesAbove(trigger.RisesAbove);
  }, [trigger.RisesAbove, trigger.FallsBelow]);

  const handleBoundChange = (val: string, type: 'FallsBelow' | 'RisesAbove') => {
    // Allow only numbers, a single decimal point, and a leading minus sign
    const numericOnly = val
      .replace(/[^0-9.\-]/g, '')
      .replace(/(?!^)-/g, '')
      .replace(/(\..*)\./g, '$1');

    if (type === 'FallsBelow') setFallsBelow(numericOnly);
    if (type === 'RisesAbove') setRisesAbove(numericOnly);

    const payload = numericOnly === '' ? null : numericOnly;
    onChange(payload, type);
  };

  function ShowFallsBelow(): boolean {
    let a = Abilities.find((a) => a.id === trigger.AbilityId);
    return (
      trigger.AbilityId > 0 &&
      (a?.triggerType === AbilityTriggerTypes.FallsBelow ||
        a?.triggerType === AbilityTriggerTypes.Both)
    );
  }

  function ShowRisesAbove(): boolean {
    let a = Abilities.find((a) => a.id === trigger.AbilityId);
    return (
      trigger.AbilityId > 0 &&
      (a?.triggerType === AbilityTriggerTypes.RisesAbove ||
        a?.triggerType === AbilityTriggerTypes.Both)
    );
  }

  function ShowOr(): boolean {
    let a = Abilities.find((a) => a.id === trigger.AbilityId);
    return trigger.AbilityId > 0 && a?.triggerType === AbilityTriggerTypes.Both;
  }

  function ShowUnits(): boolean {
    let a = Abilities.find((a) => a.id === trigger.AbilityId);
    trigger.Units = trigger?.Units || GetUnits()?.[0]?.key;
    return trigger.AbilityId > 0 && (a?.units?.length || 0) > 1;
  }

  function GetUnits(): any[] {
    let a = Abilities.find((a) => a.id === trigger.AbilityId);
    return a?.units || [];
  }

  const requiresRoundedClass = (): boolean => {
    let a = Abilities.find((a) => a.id === trigger.AbilityId);
    const abilityName = a?.name?.toLowerCase();
    return ['temperature', 'pressure', 'frequency'].includes(abilityName || '');
  };

  return (
    <React.Fragment>
      <Form.Group as={Row} className="mb-3">
        <Form.Label column sm="2" htmlFor="condition" className="pe-0">
          Condition <span className="text-danger">*</span>
        </Form.Label>
        <Col>
          <Select2
            inputId="condition"
            className="w-100"
            classNamePrefix="react-select"
            isDisabled={disabled}
            isClearable={false}
            options={abilityOptions}
            value={selectedAbilityOption}
            placeholder="-- Select Ability --"
            onChange={(opt: any) => {
              trigger.Units = undefined;

              const id = opt?.value;
              if (id != null) {
                onChange(Number(id), 'AbilityId');
              }
            }}
            styles={selectBaseStyles}
          />

          {showConditionInvalid ? (
            <div className="invalid-feedback d-block">* Please select a Condition</div>
          ) : null}
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mb-3 flex-nowrap">
        {(ShowFallsBelow() || ShowRisesAbove()) && (
          <Col sm="2">
            <Form.Label column htmlFor="condition">
              Bounds <span className="text-danger">*</span>
            </Form.Label>
          </Col>
        )}

        <Col sm="10">
          <div className="d-flex flex-nowrap justify-content-between align-items-start">
            {ShowFallsBelow() && (
              <Col sm="5">
                <div>
                  <InputGroup hasValidation>
                    <InputGroup.Text id="falls-below">Falls Below</InputGroup.Text>
                    <Form.Control
                      isInvalid={(isRangeInvalid || !hasFallsBelow) && isSubmitted}
                      isValid={hasFallsBelow && !isRangeInvalid && isSubmitted}
                      aria-describedby="falls-below"
                      type="text"
                      className="falls-below-input"
                      onKeyDown={(e) => {
                        const allowed = [
                          'Backspace',
                          'Delete',
                          'ArrowLeft',
                          'ArrowRight',
                          'Tab',
                          '.',
                          '-',
                        ];
                        if (!/^\d$/.test(e.key) && !allowed.includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      disabled={disabled}
                      value={fallsBelow ?? ''}
                      onChange={(e) => handleBoundChange(e.target.value, 'FallsBelow')}
                    />
                  </InputGroup>
                  {/* Only render the Feedback if isSubmitted is true */}
                  {isSubmitted && (
                    <Form.Control.Feedback type="invalid" className="d-block">
                      {displayError}
                    </Form.Control.Feedback>
                  )}
                </div>
              </Col>
            )}

            {ShowOr() && (
              <Col sm="1" className="d-flex justify-content-center pt-2">
                <span className="fw-bold text-muted">OR</span>
              </Col>
            )}

            <Col sm="6">
              <div>
                <InputGroup hasValidation>
                  {ShowRisesAbove() && (
                    <>
                      <InputGroup.Text id="rises-above">Rises Above</InputGroup.Text>
                      <Form.Control
                        className={`rises-above-input ${requiresRoundedClass() ? 'rounded-0' : ''}`}
                        isInvalid={(isRangeInvalid || !hasRisesAbove) && isSubmitted}
                        isValid={hasRisesAbove && !isRangeInvalid && isSubmitted}
                        aria-describedby="rises-above"
                        onKeyDown={(e) => {
                          const allowed = [
                            'Backspace',
                            'Delete',
                            'ArrowLeft',
                            'ArrowRight',
                            'Tab',
                            '.',
                            '-',
                          ];
                          if (!/^\d$/.test(e.key) && !allowed.includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        type="text"
                        disabled={disabled}
                        value={risesAbove ?? ''}
                        onChange={(e) => handleBoundChange(e.target.value, 'RisesAbove')}
                      />
                    </>
                  )}

                  {ShowUnits() && (
                    <div className="flex-grow-1">
                      <Select2
                        inputId="units"
                        classNamePrefix="select"
                        isDisabled={disabled}
                        isClearable={false}
                        placeholder="Units"
                        options={unitOptions}
                        value={selectedUnitOption}
                        onChange={(opt: any) => {
                          const value = opt?.value;
                          if (value != null) onChange(value, 'Units');
                        }}
                        styles={selectRightInGroupStyles}
                      />
                    </div>
                  )}
                </InputGroup>

                {/* Only render the Feedback if isSubmitted is true */}
                {isSubmitted && (
                  <Form.Control.Feedback type="invalid" className="d-block">
                    {displayError}
                  </Form.Control.Feedback>
                )}
              </div>
            </Col>
          </div>
        </Col>
      </Form.Group>
    </React.Fragment>
  );
}
